import React, {useContext, useEffect, useState} from 'react';
import axios from 'axios';
import moment from 'moment';
import {Link, useNavigate} from 'react-router-dom';
import {API_URL} from '../../constants';
import {AuthContext} from '../AuthContext';
import Pagination from '../Pagination';
import withAuth from '../../auth';

const statusBadgeClass = (status) => {
    switch (status) {
        case 'active':
        case 'trialing':
            return 'badge-success';
        case 'past_due':
        case 'unpaid':
            return 'badge-danger';
        case 'incomplete':
            return 'badge-warning';
        default:
            return 'badge-secondary';
    }
};

const formatStatus = (status) => status ? status.replace(/_/g, ' ') : 'No subscription';

export const WhitelabelList = () => {
    const {token, setToken} = useContext(AuthContext);
    const navigate = useNavigate();
    const [whitelabels, setWhitelabels] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [search, setSearch] = useState('');
    const [appliedSearch, setAppliedSearch] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);

    useEffect(() => {
        const fetchWhitelabels = async () => {
            setLoading(true);
            setError('');

            try {
                const response = await axios.get(`${API_URL}/api/admin/whitelabels`, {
                    params: {
                        page: currentPage,
                        ...(appliedSearch ? {search: appliedSearch} : {}),
                    },
                    headers: {Authorization: `Bearer ${token}`},
                });

                setWhitelabels(response.data.data);
                setTotalPages(response.data.last_page || 1);
            } catch (error) {
                if (error.response?.status === 401) {
                    localStorage.removeItem('authToken');
                    setToken(null);
                    navigate('/');
                    return;
                }

                if (error.response?.status === 403) {
                    setError('You do not have permission to view whitelabels.');
                } else if (error.response?.data?.message) {
                    setError(error.response.data.message);
                } else {
                    setError('An error occurred while loading whitelabels. Please try again later.');
                }
            } finally {
                setLoading(false);
            }
        };

        fetchWhitelabels();
    }, [appliedSearch, currentPage, navigate, setToken, token]);

    const handleSearch = (event) => {
        event.preventDefault();
        setCurrentPage(1);
        setAppliedSearch(search.trim());
    };

    return (
        <div className="container-fluid mt-3">
            <h2 className="text-center">Whitelabels</h2>

            <form className="form-inline justify-content-center my-3" onSubmit={handleSearch}>
                <label className="sr-only" htmlFor="whitelabel-search">Search whitelabels</label>
                <div className="input-group">
                    <input
                        id="whitelabel-search"
                        className="form-control"
                        type="search"
                        placeholder="Name, ID, or manager"
                        value={search}
                        onChange={(event) => setSearch(event.target.value)}
                    />
                    <div className="input-group-append">
                        <button className="btn btn-primary" type="submit">
                            <i className="fa fa-search" aria-hidden="true"/> <span>Search</span>
                        </button>
                    </div>
                </div>
            </form>

            {error && <div className="alert alert-danger">{error}</div>}

            {loading ? (
                <div className="d-flex justify-content-center align-items-center" style={{height: '60vh'}}>
                    <div className="spinner-border" role="status">
                        <span className="sr-only">Loading...</span>
                    </div>
                </div>
            ) : (
                <>
                    <div className="table-responsive">
                        <table className="table table-sm table-striped table-hover text-center">
                            <thead>
                            <tr>
                                <th>ID</th>
                                <th>Whitelabel</th>
                                <th>Managers</th>
                                <th>Subscription</th>
                                <th title="Allowed payments">Payments</th>
                                <th>Created</th>
                            </tr>
                            </thead>
                            <tbody>
                            {whitelabels.map((whitelabel) => (
                                <tr key={whitelabel.id}>
                                    <td className="align-middle position-relative">
                                        <Link
                                            to={`/whitelabels/${whitelabel.id}`}
                                            aria-label={`Open ${whitelabel.name} company (ID ${whitelabel.id})`}
                                            className="d-block text-primary stretched-link"
                                            style={{textDecoration: 'underline'}}
                                        >
                                            {whitelabel.id}
                                        </Link>
                                    </td>
                                    <td className="align-middle text-center position-relative">
                                        <Link
                                            to={`/whitelabels/${whitelabel.id}`}
                                            aria-label={`Open ${whitelabel.name} company`}
                                            className="d-block text-primary stretched-link"
                                            style={{textDecoration: 'underline'}}
                                        >
                                            <div className="d-flex align-items-center justify-content-center" style={{minWidth: '180px'}}>
                                                {whitelabel.logo_url && (
                                                    <img
                                                        src={whitelabel.logo_url}
                                                        alt=""
                                                        className="mr-2"
                                                        style={{width: '42px', height: '42px', objectFit: 'contain'}}
                                                    />
                                                )}
                                            </div>
                                            <strong>{whitelabel.name}</strong>
                                        </Link>
                                    </td>
                                    <td className="align-middle text-left">
                                        <small className="d-block text-muted mb-1">
                                            {whitelabel.managers_count} {whitelabel.managers_count === 1 ? 'manager' : 'managers'}
                                        </small>
                                        {whitelabel.managers.length > 0 ? whitelabel.managers.map((manager) => (
                                            <div key={manager.id} className="mb-1">
                                                <div>{manager.name}</div>
                                                <a href={`mailto:${manager.email}`}>{manager.email}</a>
                                                {manager.phone && <div>{manager.phone}</div>}
                                            </div>
                                        )) : <span className="text-muted">No managers</span>}
                                    </td>
                                    <td className="align-middle">
                                        <span className={`badge ${statusBadgeClass(whitelabel.subscription?.status)}`}>
                                            {formatStatus(whitelabel.subscription?.status)}
                                        </span>
                                        {whitelabel.subscription?.plan && (
                                            <div className="mt-1">
                                                {whitelabel.subscription.plan.name}
                                            </div>
                                        )}
                                        {whitelabel.subscription?.current_period_end && (
                                            <small className="d-block text-muted">
                                                Through {moment(whitelabel.subscription.current_period_end).format('YYYY-MM-DD')}
                                            </small>
                                        )}
                                    </td>
                                    <td className="align-middle text-nowrap">
                                        <span className={`badge mr-1 ${whitelabel.allow_card_payments ? 'badge-success' : 'badge-light'}`}>
                                            Card
                                        </span>
                                        <span className={`badge ${whitelabel.allow_invoice_payments ? 'badge-success' : 'badge-light'}`}>
                                            Invoice
                                        </span>
                                    </td>
                                    <td className="align-middle text-nowrap">
                                        {moment(whitelabel.created_at).format('YYYY-MM-DD HH:mm')}
                                    </td>
                                </tr>
                            ))}
                            {whitelabels.length === 0 && (
                                <tr>
                                    <td colSpan="6" className="py-4 text-muted">No whitelabels found.</td>
                                </tr>
                            )}
                            </tbody>
                        </table>
                    </div>

                    {totalPages > 1 && (
                        <Pagination
                            currentPage={currentPage}
                            totalPages={totalPages}
                            handlePageChange={setCurrentPage}
                        />
                    )}
                </>
            )}
        </div>
    );
};

export default withAuth(WhitelabelList);
